import classNames from 'classnames';
import React from 'react';
import { useGetAllCards } from '../../../hooks/apolloHooks/useGetAllCards';
import { CardInvoice, CurrentCustomer } from '../../../integrations/allkort/graphql/interface/Customer';
import { StatementPdfData } from '../../../integrations/allkort/graphql/interface/StatementPdf';
import { scrollToElement } from '../../../utils/scrollUtils';
import InvoiceSummaryInfo from '../../elements/invoicesstatements/InvoiceSummaryInfo';
import LatestInvoice from '../../elements/invoicesstatements/LatestInvoice';
import StatementSummaryInfo from '../../elements/invoicesstatements/StatementSummaryInfo';
import { DownArrowIcon } from '../../svgs/ExpressonSVGs';

type InvoicesStatementsHeaderProps = {
	wrapperClasses?: string;
	customerData?: CurrentCustomer;
	latestInvoice?: CardInvoice | null;
	unpaidInvoicesCount: number;
	invoiceCount?: number;
	firstUnpaidInvoice?: CardInvoice | null;
	totalOverdueSum?: number;
	overdueInvoicesCount?: number;
	latestStatementPdfData?: StatementPdfData | null;
	hasNoStatements?: boolean;
	invoiceTabActive?: boolean;
	setInvoiceTabActive?: (active: boolean) => void;
	paidRemainsComplete?: boolean;
}

const InvoicesStatementsHeader: React.FC<InvoicesStatementsHeaderProps> = ({
	wrapperClasses = 'white-pale-section shadow-one',
	latestInvoice,
	unpaidInvoicesCount,
	invoiceCount = 0,
	firstUnpaidInvoice,
	totalOverdueSum = 0,
	overdueInvoicesCount = 0,
	latestStatementPdfData = null,
	hasNoStatements = false,
	invoiceTabActive = true,
	setInvoiceTabActive = () => { },
	paidRemainsComplete = false
}) => {

	const { cards } = useGetAllCards()
	const cardTypes = new Set(cards.map(card => card.type.shortName))

	// Check which card types customer has that translates to invoice/statement services
	const hasP = cardTypes.has('P')  // Smartkort
	const hasK = cardTypes.has('K')  // Tankkort/Företagskort

	// Determine header names based on card services the customer has.
	const getInvoiceStatementNames = (hasP: boolean, hasK: boolean): string => {
		// Return appropriate label based on card types
		if (hasP && hasK) {
			return 'Dina fakturor & kontobesked'
		} else if (hasP) {
			return 'Dina kontobesked'
		} else if (hasK) {
			return 'Dina fakturor'
		}

		// Fallback if no cards found not expected to happen since this component should be hidden if no cards, but just in case:
		return 'Dina Fakturor och kontobesked'
	}

	const getInvoiceStatementSubtext = (hasP: boolean, hasK: boolean): string => {
		// Return appropriate subtext based on card types
		if (hasP && hasK) {
			return 'Här kan du se och ladda ner dina fakturor och kontobesked.'
		} else if (hasP) {
			return 'Här kan du se och ladda ner dina kontobesked summerade för varje månad.'
		} else if (hasK) {
			return 'Här kan du se kommande, påminnelser och fakturahistorik.'
		}

		// Fallback if no cards found not expected to happen since this component should be hidden if no cards, but just in case:
		return 'Här kan du se och ladda ner dina fakturor och kontobesked.'
	}

	/**
	 * handleScrollToInvoicesClick
	 * 
	 * Switch to invoices tab if needed, then scroll to invoice list when multiple unpaid invoices notice button is clicked
	 */
	const handleScrollToInvoicesClick = () => {
		// Switch to invoices tab if currently viewing statements
		if (!invoiceTabActive) {
			setInvoiceTabActive(true);
			// Add small delay to allow DOM to render before scrolling
			setTimeout(() => {
				scrollToElement('invoice-list');
			}, 100);
		} else {
			// Already on invoices tab, scroll immediately
			scrollToElement('invoice-list');
		}
	}

	return (
		<div
			className={classNames(
				'Row__Inner',
				'inv-state-header',
				wrapperClasses
			)}
		>
			<div className="inv-state-overview">
				<div className="inv-state-excerpt">
					<h1>{getInvoiceStatementNames(hasP, hasK)}</h1>
					<p>{getInvoiceStatementSubtext(hasP, hasK)}</p>
				</div>
				<div className="inv-state-horizontal-divider"></div>
				{
					/**
					 * Show either latest invoice or multiple unpaid invoice notice
					 */
				}
				{unpaidInvoicesCount > 1 ? (
					<div className="inv-state-multiple-unpaid-wrapper has-dark-bg">
						<p className="inv-state-multiple-unpaid-notice">
							Observera att du har flera obetalda fakturor. Vänligen se över dessa nedan i fakturalistan.
						</p>
						<button
							type="button"
							className={classNames(
								'scroll-to-invoces-button',
								'dashboard-button',
							)}
							aria-label='Visa obetalda fakturor'
							onClick={handleScrollToInvoicesClick}
						>
							Visa fakturor
							<DownArrowIcon />
						</button>
					</div>

				) : (
					latestInvoice !== null && (
						<div className="inv-state-latest-invoice-wrapper">
							<LatestInvoice latestInvoice={latestInvoice} paidRemainsComplete={paidRemainsComplete} />
						</div>
					)
				)}

				<div className={classNames(
					'inv-state-summary-info',
					{ 'left-align': invoiceCount === 0 }
				)}>
					<InvoiceSummaryInfo
						invoiceCount={invoiceCount}
						unpaidInvoicesCount={unpaidInvoicesCount}
						firstUnpaidInvoice={firstUnpaidInvoice}
						totalOverdueSum={totalOverdueSum}
						overdueInvoicesCount={overdueInvoicesCount}
						invoiceTabActive={invoiceTabActive}
						setInvoiceTabActive={setInvoiceTabActive}
					/>
					<StatementSummaryInfo
						latestStatementPdfData={latestStatementPdfData}
						hasNoStatements={hasNoStatements}
						hasSmartCard={hasP}
					/>
				</div>
			</div>
		</div>
	)
}

export default InvoicesStatementsHeader;
